import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { supabase } from '../supabaseClient'
import { slugifyTitle } from '../lib/helpers'
import TagInput from '../components-admin/TagInput'
import ImageUploader from '../components-admin/ImageUploader'

export default function CaseEditorPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [title, setTitle] = useState('')
  const [client, setClient] = useState('')
  const [sector, setSector] = useState('public')
  const [subSector, setSubSector] = useState('')
  const [description, setDescription] = useState('')
  const [featuredResult, setFeaturedResult] = useState('')
  const [tags, setTags] = useState([])
  const [coverImageUrl, setCoverImageUrl] = useState('')
  const [status, setStatus] = useState('draft')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isEdit) return
    supabase.from('cases').select('*').eq('id', id).single().then(({ data }) => {
      if (!data) return
      setTitle(data.title)
      setClient(data.client || '')
      setSector(data.sector || 'public')
      setSubSector(data.sub_sector || '')
      setDescription(data.description || '')
      setFeaturedResult(data.featured_result || '')
      setTags(data.tags || [])
      setCoverImageUrl(data.cover_image_url || '')
      setStatus(data.status || 'draft')
    })
  }, [id, isEdit])

  const handleSave = async (e) => {
    e.preventDefault()
    setError('')
    if (!title.trim()) return setError('O título é obrigatório.')
    setSaving(true)

    const slug = slugifyTitle(title)
    const payload = {
      title, slug, client,
      sector,
      sub_sector: subSector,
      description,
      featured_result: featuredResult,
      tags,
      cover_image_url: coverImageUrl,
      status,
    }

    if (isEdit) {
      await supabase.from('cases').update(payload).eq('id', id)
    } else {
      await supabase.from('cases').insert(payload)
    }
    setSaving(false)
    navigate('/admin/cases')
  }

  const inputClass = "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-[#bef102] focus:ring-1 focus:ring-[#bef102] focus:outline-none transition-colors"
  const labelClass = "block text-xs font-semibold text-gray-500 mb-1"

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/admin/cases')} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-extrabold font-display text-gray-900">{isEdit ? 'Editar case' : 'Novo case'}</h1>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

      <form onSubmit={handleSave} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div>
              <label className={labelClass}>Título</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Cliente</label>
                <input value={client} onChange={(e) => setClient(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Setor</label>
                <select value={sector} onChange={(e) => setSector(e.target.value)} className={inputClass}>
                  <option value="public">Público</option>
                  <option value="private">Privado</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Área de atuação</label>
              <input value={subSector} onChange={(e) => setSubSector(e.target.value)} placeholder="Ex: Educação, Saúde, Indústria" className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Descrição</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Resultado em destaque</label>
              <input value={featuredResult} onChange={(e) => setFeaturedResult(e.target.value)} className={inputClass} />
            </div>
          </div>

          <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 h-fit shadow-sm">
            <div>
              <label className={labelClass}>Imagem de capa</label>
              <ImageUploader value={coverImageUrl} onChange={setCoverImageUrl} />
            </div>
            <div>
              <label className={labelClass}>Tags</label>
              <TagInput tags={tags} onChange={setTags} />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputClass}>
                <option value="draft">Rascunho</option>
                <option value="published">Publicado</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="submit" disabled={saving} className="rounded-lg bg-[#bef102] px-5 py-2.5 text-sm font-bold text-[#0d1f12] hover:bg-[#a8d800] transition-colors disabled:opacity-50">
            {saving ? 'Salvando…' : 'Salvar case'}
          </button>
          <button type="button" onClick={() => navigate('/admin/cases')} className="rounded-lg border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  )
}
